/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useState } from 'react';

/** Палитра категорий в фиксированном порядке (проверена на различимость при нарушениях цветового зрения). */
export const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
export interface Row { label: string; value: number }

const fmt = (v: number, money?: boolean) => money ? v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽' : v.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
const short = (v: number) => Math.abs(v) >= 1e6 ? (v / 1e6).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + ' млн' : Math.abs(v) >= 1e3 ? (v / 1e3).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' тыс' : fmt(v);
function niceMax(v: number) { if (v <= 0) return 1; const p = 10 ** Math.floor(Math.log10(v)); const n = v / p; return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p; }

function Tip({ left, text }: { left: string; text: string }) {
  return <div className="pointer-events-none absolute top-0 z-10 -translate-x-1/2 whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-800 shadow" style={{ left }}>{text}</div>;
}

/** Горизонтальные столбцы: подписи категорий слева читаются без поворота. */
export function BarChart({ rows, money }: { rows: Row[]; money?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = niceMax(Math.max(...rows.map((r) => r.value), 0));
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,38%)_1fr] items-center gap-2 text-xs" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
          <div className="truncate text-slate-600" title={r.label}>{r.label}</div>
          <div className="flex items-center gap-2">
            <div className="h-4 rounded-r" style={{ width: `${Math.max(1, (r.value / max) * 100)}%`, background: SERIES[0], opacity: hover === null || hover === i ? 1 : 0.55 }} />
            <span className="tabular-nums text-slate-700">{short(r.value)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Линия по времени с перекрестием и подсказкой. */
export function LineChart({ rows, money }: { rows: Row[]; money?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 520, H = 180, L = 44, B = 22, T = 10;
  const max = niceMax(Math.max(...rows.map((r) => r.value), 0));
  const x = (i: number) => L + (rows.length <= 1 ? (W - L) / 2 : (i * (W - L - 8)) / (rows.length - 1));
  const y = (v: number) => T + (1 - v / max) * (H - T - B);
  const d = rows.map((r, i) => `${i ? 'L' : 'M'}${x(i)},${y(r.value)}`).join(' ');
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => { const b = (e.currentTarget as SVGSVGElement).getBoundingClientRect(); const px = ((e.clientX - b.left) / b.width) * W; let best = 0; rows.forEach((_, i) => { if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i; }); setHover(rows.length ? best : null); }}>
        {[0, 0.5, 1].map((f) => <g key={f}><line x1={L} x2={W} y1={y(max * f)} y2={y(max * f)} stroke="#e7e5e4" /><text x={L - 6} y={y(max * f) + 4} textAnchor="end" fontSize="10" fill="#78716c">{short(max * f)}</text></g>)}
        {rows.map((r, i) => (rows.length <= 8 || i % Math.ceil(rows.length / 8) === 0) && <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="#78716c">{r.label}</text>)}
        <path d={d} fill="none" stroke={SERIES[0]} strokeWidth="2" />
        {hover !== null && <line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} stroke="#a8a29e" strokeDasharray="3 3" />}
        {rows.map((r, i) => <circle key={i} cx={x(i)} cy={y(r.value)} r={hover === i ? 5 : 3} fill={SERIES[0]} stroke="#fff" strokeWidth="2" />)}
      </svg>
      {hover !== null && rows[hover] && <Tip left={`${(x(hover) / W) * 100}%`} text={`${rows[hover].label}: ${fmt(rows[hover].value, money)}`} />}
    </div>
  );
}

/** Кольцо долей; больше 6 категорий сворачивается в «Прочее». */
export function DonutChart({ rows, money }: { rows: Row[]; money?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  let data = [...rows].sort((a, b) => b.value - a.value);
  if (data.length > 6) data = [...data.slice(0, 5), { label: 'Прочее', value: data.slice(5).reduce((s, r) => s + r.value, 0) }];
  const total = data.reduce((s, r) => s + r.value, 0) || 1;
  let acc = 0;
  const R = 60, r0 = 38, C = 70;
  const arc = (a0: number, a1: number) => {
    const p = (a: number, rad: number) => [C + rad * Math.sin(a), C - rad * Math.cos(a)];
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const [x0, y0] = p(a0, R), [x1, y1] = p(a1, R), [x2, y2] = p(a1, r0), [x3, y3] = p(a0, r0);
    return `M${x0},${y0} A${R},${R} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${r0},${r0} 0 ${large} 0 ${x3},${y3} Z`;
  };
  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg viewBox="0 0 140 140" className="h-36 w-36 shrink-0">
        {data.map((d, i) => { const a0 = (acc / total) * 2 * Math.PI; acc += d.value; const a1 = Math.min((acc / total) * 2 * Math.PI, 2 * Math.PI - 0.0001);
          return <path key={i} d={arc(a0, a1)} fill={SERIES[i]} stroke="#fff" strokeWidth="2" opacity={hover === null || hover === i ? 1 : 0.5} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}><title>{`${d.label}: ${fmt(d.value, money)}`}</title></path>; })}
        <text x="70" y="74" textAnchor="middle" fontSize="13" fontWeight="600" fill="#1c1917">{short(hover === null ? total : data[hover].value)}</text>
      </svg>
      <ul className="min-w-0 flex-1 space-y-1 text-xs">
        {data.map((d, i) => <li key={i} className="flex items-center gap-2" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
          <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: SERIES[i] }} /><span className="truncate text-slate-700">{d.label}</span>
          <span className="ml-auto tabular-nums text-slate-500">{fmt(d.value, money)} · {Math.round((d.value / total) * 100)}%</span></li>)}
      </ul>
    </div>
  );
}

export function Kpi({ rows, money }: { rows: Row[]; money?: boolean }) {
  return <div className="py-4 text-3xl font-semibold tabular-nums text-slate-900">{fmt(rows[0]?.value ?? 0, money)}</div>;
}

export function DataTable({ rows, money }: { rows: Row[]; money?: boolean }) {
  return (
    <table className="w-full text-xs"><tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-100"><td className="py-1 pr-2 text-slate-700">{r.label}</td><td className="py-1 text-right tabular-nums">{fmt(r.value, money)}</td></tr>)}</tbody></table>
  );
}

export function Chart({ chart, rows, money }: { chart: string; rows: Row[]; money?: boolean }) {
  if (!rows.length) return <div className="py-6 text-center text-sm text-slate-400">Нет данных за период</div>;
  if (chart === 'kpi') return <Kpi rows={rows} money={money} />;
  if (chart === 'line') return <LineChart rows={rows} money={money} />;
  if (chart === 'donut') return <DonutChart rows={rows} money={money} />;
  if (chart === 'table') return <DataTable rows={rows} money={money} />;
  return <BarChart rows={rows} money={money} />;
}
